import type { VariableDataType } from "@/components/rule-canvas-builder/hooks/useRuleVariables";

export type DataTypeIconKey =
	| "boolean"
	| "number"
	| "text"
	| "decimal"
	| "date"
	| "datetime"
	| "time";

export interface VariableDataTypeOption {
	id: number;
	label: string;
	value: VariableDataType;
	/** Maps to `@/svgs/*.svg` via `global/components/icons` wrappers (e.g. NumberIcon). */
	iconKey: DataTypeIconKey;
}

export const getDefaultVariableValue = (dataType: VariableDataType) => {
	switch (dataType) {
		case "boolean":
			return "true";
		case "number":
		case "decimal":
			return "0";
		default:
			return "";
	}
};

/** Default SVG icon key when only `VariableDataType` is known (no selected option row). */
export function iconKeyForVariableDataType(value: VariableDataType): DataTypeIconKey {
	switch (value.toLowerCase()) {
		case "boolean":
			return "boolean";
		case "number":
			return "number";
		case "text":
			return "text";
		case "decimal":
			return "decimal";
		case "date":
			return "date";
		case "datetime":
			return "datetime";
		case "time":
			return "time";
		default:
			return "text";
	}
}

/**
 * Derives value-field semantics + pill icon from the current id and the option row
 * resolved by DataTypeField (guards against stale resolvedOption when ids disagree).
 */
export function getValueFieldDataTypePresentation(
	dataTypeId: number | null,
	resolvedOption: VariableDataTypeOption | null,
): { dataType: VariableDataType | null; iconKey: DataTypeIconKey } {
	if (dataTypeId == null) {
		return { dataType: null, iconKey: "text" };
	}
	const idsMatch = (a: number, b: number | undefined) => Number(a) === Number(b);
	const aligned =
		resolvedOption != null && idsMatch(dataTypeId, resolvedOption.id) ? resolvedOption : null;
	const dataType =
		aligned?.value ?? VARIABLE_DATA_TYPE_OPTIONS.find((o) => idsMatch(dataTypeId, o.id))?.value ?? null;
	const iconKey: DataTypeIconKey =
		aligned?.iconKey ??
		VARIABLE_DATA_TYPE_OPTIONS.find((o) => idsMatch(dataTypeId, o.id))?.iconKey ??
		(dataType != null ? iconKeyForVariableDataType(dataType) : "text");
	return { dataType, iconKey };
}

export const VARIABLE_DATA_TYPE_OPTIONS: VariableDataTypeOption[] = [
	{ id: 1, label: "Text", value: "text", iconKey: "text" },
	{ id: 2, label: "Boolean", value: "boolean", iconKey: "boolean" },
	{ id: 3, label: "Number", value: "number", iconKey: "number" },
	{ id: 4, label: "Decimal", value: "decimal", iconKey: "decimal" },
	{ id: 5, label: "Date", value: "date", iconKey: "date" },
	{ id: 6, label: "Date Time", value: "datetime", iconKey: "datetime" },
	{ id: 7, label: "Time", value: "time", iconKey: "time" },
];
